import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {makeAutoObservable, observable, IReactionDisposer, runInAction} from "mobx";
import type {IGameBarStore, IGameBar} from "data/stores/game_bar/game_bar.store";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {ContestStatus, GameLevel, RequestState, ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage} from "data/utils";
import {IRosterItem} from "data/types/roster";

interface IParams {
	contestId?: number;
}

export interface IRoundStatsController extends ViewController<IParams> {
	get i18n(): ILocalizationStore;
	get isLoading(): boolean;
	get contestIdForRoundStats(): number;
	get isComplete(): boolean;
	get gameBar(): IGameBar | undefined;
	get roster(): IRosterItem | undefined;
	get teamName(): string;
}

@injectable()
export class RoundStatsController implements IRoundStatsController {
	@observable private _contestId: number = 0;
	@observable private _requestState: RequestState = RequestState.IDLE;

	@observable _reactionDisposers: IReactionDisposer[] = [];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.GameBarStore) private readonly _gameBarStore: IGameBarStore,
		@inject(Bindings.ContestsStore) private readonly _contestStore: IContestsStore,
		@inject(Bindings.RosterStore) private readonly _rosterStore: IRosterStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get contestIdForRoundStats(): number {
		return this._contestId;
	}

	get isLoading() {
		return this._requestState === RequestState.PENDING;
	}

	get roster(): IRosterItem | undefined {
		return this._rosterStore.currentRoster;
	}

	get teamName(): string {
		return this._userStore.user?.displayName || "-";
	}

	get level() {
		return this.gameBar?.level || GameLevel.SINGLE_A;
	}

	get contest() {
		return this._contestStore.getContestById(this._contestId);
	}

	get isComplete(): boolean {
		return this.contest?.status === ContestStatus.Complete;
	}

	get gameBar() {
		return this._gameBarStore.gameBar;
	}

	dispose(): void {
		this._reactionDisposers.forEach((dispose) => dispose());
	}

	init(param: IParams): void {
		this._contestId = param.contestId || this._contestStore.currentContest?.id || 0;

		this._rosterStore.updateContestId(this._contestId);

		void this._rosterStore.fetchRoster();
		void this._fetchGameBar(this._contestId);
	}

	onChange(param: IParams) {
		this.init(param);
	}

	private async _fetchGameBar(contestId?: number) {
		try {
			this._requestState = RequestState.PENDING;
			if (contestId) {
				await this._gameBarStore.fetchGameBar(contestId);
			}

			runInAction(() => {
				this._requestState = RequestState.SUCCESS;
			});
		} catch (e) {
			runInAction(() => {
				this._requestState = RequestState.ERROR;
			});

			this._modalsStore.showModal(ModalType.ERROR, {
				message: extractErrorMessage(e),
			});
		}
	}
}
